import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  ETFCategory,
  ETFCategoryDetail,
  ETFReturn,
  ETFCategoryReturn,
  ETFHoldingsResponse,
} from './types';

const baseUrl = process.env.REACT_APP_API_URL || '';

// The one place the holdings URL lives. Swap for
// `etfcategories/${slug}/holdings?ticker=${ticker}` if the backend ships the
// flat form instead of the nested one.
export const etfHoldingsPath = (slug: string, ticker: string) =>
  `etfcategories/${slug}/members/${ticker}/holdings`;

// Sprint 18. The table asks for one page at a time. The endpoint sorts and
// searches before it cuts the page, so both cover the whole table. A
// request with no page gets every row, which is what the CSV export asks.
export const HOLDINGS_PAGE_SIZE = 100;

export interface ETFHoldingsArgs {
  slug: string;
  ticker: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  dir?: 'asc' | 'desc';
  search?: string;
}

export const etfHoldingsUrl = (args: ETFHoldingsArgs): string => {
  const params = new URLSearchParams();
  if (args.page) {
    params.set('page', String(args.page));
    params.set('page_size', String(args.pageSize ?? HOLDINGS_PAGE_SIZE));
  }
  if (args.sort) params.set('sort', args.sort);
  if (args.dir) params.set('dir', args.dir);
  if (args.search) params.set('search', args.search);
  const query = params.toString();
  return `${etfHoldingsPath(args.slug, args.ticker)}${query ? `?${query}` : ''}`;
};

export const etfCategoryApi = createApi({
  reducerPath: 'etfCategoryApi',
  baseQuery: fetchBaseQuery({ baseUrl }),
  tagTypes: ['ETFCategory', 'ETFReturn'],
  endpoints: (builder) => ({
    // Get all ETF categories with latest returns
    getETFCategories: builder.query<ETFCategory[], void>({
      query: () => 'etfcategories',
      transformResponse: (response: { results: ETFCategory[] }) => response.results,
      providesTags: ['ETFCategory'],
    }),

    // Get single category with members
    getETFCategory: builder.query<ETFCategoryDetail, string>({
      query: (slug) => `etfcategories/${slug}`,
      providesTags: (result, error, slug) => [{ type: 'ETFCategory', id: slug }],
    }),

    // Get category members with returns
    getETFCategoryMembers: builder.query<ETFReturn[], string>({
      query: (slug) => `etfcategories/${slug}/members`,
      providesTags: (result, error, slug) => [{ type: 'ETFReturn', id: slug }],
    }),

    // Get category history
    getETFCategoryHistory: builder.query<ETFCategoryReturn[], { slug: string; days?: number }>({
      query: ({ slug, days = 30 }) => `etfcategories/${slug}/history?days=${days}`,
    }),

    // Get the stocks inside one ETF on a card, with their returns
    getETFHoldings: builder.query<ETFHoldingsResponse, ETFHoldingsArgs>({
      query: (args) => etfHoldingsUrl(args),
      providesTags: (result, error, { slug, ticker }) => [
        { type: 'ETFReturn', id: `${slug}/${ticker}` },
      ],
    }),

    // Get individual ETF returns
    getETFReturns: builder.query<ETFReturn[], string[]>({
      query: (tickers) => `etfreturns?tickers=${tickers.join(',')}`,
      providesTags: ['ETFReturn'],
    }),
  }),
});

export const {
  useGetETFCategoriesQuery,
  useGetETFCategoryQuery,
  useGetETFCategoryMembersQuery,
  useGetETFCategoryHistoryQuery,
  useGetETFHoldingsQuery,
  useLazyGetETFHoldingsQuery,
  useGetETFReturnsQuery,
} = etfCategoryApi;

export default etfCategoryApi;
